import { FC, useState } from 'react'
import { KTIcon } from '../../../../_metronic/helpers'
import { credentialToJson, isWebAuthnAvailable, toCreationOptions } from '../../auth/core/webauthn'
import { addPasskey, getPasskeyCreationOptions, removePasskey } from '../core/_requests'
import { getErrorCode } from '../../../common/api/errors'
import { accountErrorText, dataOf } from '../core/errors'
import { AccountOverview, AskForPassword, DEFAULT_PASSKEY_NAME, PASSKEY_NAME_MAX_LENGTH, PasskeyItem } from '../core/_models'
import { formatMskDateTime } from '../../matching/routes/Dashboard/core/mskTime'

type Props = {
  overview: AccountOverview
  ask: AskForPassword
  onChange: (overview: AccountOverview) => void
}

const date = (value: string | null) => (value ? formatMskDateTime(value) : '—')

/** Passkeys spare the person the authenticator code. They exist only on top of the authenticator app. */
export const PasskeysCard: FC<Props> = ({ overview, ask, onChange }) => {
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const add = () =>
    ask({
      title: 'Добавление ключа',
      action: async (password) => {
        setError(null)
        let options: any
        try {
          options = dataOf(await getPasskeyCreationOptions(password)).options
        } catch (e) {
          // not the password: this address (plain http, an unusual origin) cannot hold a passkey
          if (getErrorCode(e) === 'SECOND_FACTOR_INVALID') {
            setError('На этом адресе кабинета ключи доступа недоступны.')
            return
          }
          if (getErrorCode(e) === 'PASSKEY_LIMIT_REACHED') {
            setError(accountErrorText(e))
            return
          }

          throw e
        }

        // the password is confirmed: from here a failure belongs to the key, not to the prompt
        try {
          const credential = (await navigator.credentials.create({
            publicKey: toCreationOptions(options),
          })) as PublicKeyCredential | null
          if (!credential) {
            throw new Error('no credential')
          }

          onChange(dataOf(await addPasskey(credentialToJson(credential), name.trim() || DEFAULT_PASSKEY_NAME)))
          setName('')
        } catch (e) {
          // the server said why (a limit, too many attempts); otherwise it was the browser prompt
          const refused = typeof (e as { status?: unknown } | undefined)?.status === 'number'
          setError(refused ? accountErrorText(e, 'Ключ не добавлен: он не подошёл.') : 'Ключ не добавлен: запрос отменён или ключ не подошёл.')
        }
      },
    })

  const remove = (passkey: PasskeyItem) =>
    ask({
      title: `Удаление ключа «${passkey.name}»`,
      warning: 'Второй фактор останется включён: вход будет подтверждаться кодом из приложения.',
      action: async (password) => {
        onChange(dataOf(await removePasskey(passkey.id, password)))
      },
    })

  return (
    <div className='card mb-7'>
      <div className='card-header'>
        <h3 className='card-title fw-bold'>Ключи доступа (Passkey)</h3>
      </div>
      <div className='card-body'>
        <p className='text-gray-700 mb-7'>
          Ключ — аппаратный или встроенный в устройство — подтверждает вход вместо ввода кода из
          приложения. Работает только на том адресе кабинета, где был добавлен.
        </p>

        {!overview.secondFactorEnabled ? (
          <div className='text-muted'>Сначала подключите приложение-аутентификатор.</div>
        ) : (
          <>
            {overview.passkeys.length > 0 && (
              <div className='table-responsive mb-5'>
                <table className='table align-middle gs-0 gy-2'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th>Название</th>
                      <th>Добавлен</th>
                      <th>Последний вход</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {overview.passkeys.map((passkey) => (
                      <tr key={passkey.id}>
                        <td className='fw-bold text-break'>{passkey.name}</td>
                        <td>{date(passkey.createdAt)}</td>
                        <td>{date(passkey.lastUsedAt)}</td>
                        <td className='text-end'>
                          <button
                            type='button'
                            className='btn btn-sm btn-icon btn-light-danger'
                            aria-label={`Удалить ключ «${passkey.name}»`}
                            onClick={() => remove(passkey)}
                          >
                            <KTIcon iconName='trash' className='fs-2' />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {isWebAuthnAvailable() ? (
              <div className='d-flex flex-wrap align-items-end gap-3'>
                <div>
                  <label className='form-label fw-bold' htmlFor='account_passkey_name'>
                    Название ключа
                  </label>
                  <input
                    id='account_passkey_name'
                    type='text'
                    className='form-control'
                    style={{ maxWidth: 280 }}
                    placeholder={DEFAULT_PASSKEY_NAME}
                    maxLength={PASSKEY_NAME_MAX_LENGTH}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </div>
                <button type='button' className='btn btn-primary' onClick={add}>
                  Добавить ключ
                </button>
              </div>
            ) : (
              <div className='text-muted'>Этот браузер не поддерживает ключи доступа.</div>
            )}
            {error && <div className='text-danger mt-4'>{error}</div>}
          </>
        )}
      </div>
    </div>
  )
}
